import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import config from '../../config/config.json';
import { environment } from '../../environments/environment';
import { ConfigurationService } from '../configuration/configuration.service';
import { LoggerService } from '../logger-services/logger.service';
import { RegulationNextService } from '../regulation-next/regulation-next.service';
import { TokenService } from '../../shared/services/token/token.service';

export interface RegulationNextDownloadConfig {
  griApiBaseUrl: string;
  presignedUrlPath: string;
}

export interface RegulationPresignedUrlApiResponse {
  status: string;
  code: number;
  message: string | null;
  data: {
    file_path: string;
    presigned_url: string;
    expires_in: number;
  };
}

const DEFAULT_REGULATION_NEXT_DOWNLOAD_CONFIG: RegulationNextDownloadConfig = (
  config as { RegulationNextDownload?: RegulationNextDownloadConfig }
).RegulationNextDownload ?? {
  griApiBaseUrl: 'https://api.devsecops.freyafusion.com',
  presignedUrlPath: '/gri-service/regulations/documents/presigned-url',
};

@Injectable({
  providedIn: 'root',
})
export class RegulationNextDownloadService {
  private readonly http = inject(HttpClient);
  private readonly configurationService = inject(ConfigurationService);
  private readonly loggerService = inject(LoggerService);
  private readonly regulationNextService = inject(RegulationNextService);
  private readonly tokenService = inject(TokenService);

  hasGriRegulationAccess(): boolean {
    return this.regulationNextService.hasRegulationNextAccess();
  }

  getPresignedUrl(fileName: string): Observable<string> {
    const downloadConfig = this.getDownloadConfig();
    const filePath = fileName.trim();
    const url = `${downloadConfig.griApiBaseUrl.replace(/\/$/, '')}${downloadConfig.presignedUrlPath}`;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${this.tokenService.getAccessToken()}`,
      domain: (environment.DomainName || '').toUpperCase(),
    });

    return this.http
      .post<RegulationPresignedUrlApiResponse>(url, { file_path: filePath }, { headers })
      .pipe(
        map((response) => {
          const presignedUrl = response?.data?.presigned_url;
          if (!presignedUrl || response.code !== 200) {
            throw new Error(response?.message ?? 'Failed to get regulation presigned URL');
          }
          return presignedUrl;
        }),
        catchError((error: unknown) => {
          this.loggerService.logError(
            (error as Error)?.message ?? 'Regulation presigned URL request failed',
            'RegulationNextDownloadService.getPresignedUrl'
          );
          return throwError(() => error);
        })
      );
  }

  private getDownloadConfig(): RegulationNextDownloadConfig {
    const fromRuntime = this.configurationService.configData?.RegulationNextDownload as
      | RegulationNextDownloadConfig
      | undefined;
    return fromRuntime ?? DEFAULT_REGULATION_NEXT_DOWNLOAD_CONFIG;
  }
}
